import { z } from "zod";

export const schema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export type InputType = z.infer<typeof schema>;

export type TirageAnalyseOutput = {
  tirage: {
    id: number;
    date: string;
    numeros: number[];
    anneeNumeroDeTirage: string | null;
    multiplicateur: number | null;
    numeroJokerplus: string | null;
  };
  distribution: {
    pairs: number;
    impairs: number;
    hauts: number;
    bas: number;
    consecutifs: number;
    somme: number;
    /** null pour l'anonyme quand la famille « tirage » est ouverte (calcul réservé au compte). */
    sommeMoyenne: number | null;
    ecartMoyen: number;
    densite: {
      type: "concentre" | "disperse" | "equilibre";
      ecartType: number;
    };
  };
  dizaines: { label: string; count: number }[];
  finales: { finale: number; count: number }[];
  comparaisonJ1?: {
    numerosRepetes: number[];
    similarite: number;
  };
  comparaisonJ2?: {
    numerosRepetes: number[];
    similarite: number;
  };
  seriesEnCours: {
    numero: number;
    streak: number;
  }[];
  comebacks: {
    numero: number;
    ecartAvant: number;
  }[];
  numerosAnnonces: {
    numero: number;
    annoncePar: { numero: number; count: number }[];
    totalCount: number;
  }[];
  tirageRessemblant: {
    date: string;
    numeros: number[];
    numerosCommuns: number[];
    similarite: number;
  } | null;
  tiragesRessemblants: {
    date: string;
    numeros: number[];
    numerosCommuns: number[];
    similarite: number;
  }[];
  /** null pour l'anonyme quand la famille « tirage » est ouverte (aucun calcul). */
  rangHistorique: {
    pairsPercentile: number;
    sommePercentile: number;
    message: string;
  } | null;
  navigation: {
    previousDate: string | null;
    nextDate: string | null;
  };
  periode: {
    startDate: string | null;
    endDate: string | null;
    totalTirages: number;
    // Vrai quand des dates ont été envoyées sans le niveau requis : calcul par défaut.
    periodeIgnoree: boolean;
  };
  highlights: {
    type: string;
    emoji: string;
    text: string;
    priority: number;
  }[];
  aiAnalysis: string | null;
  hasAiInsight: boolean;
  hasDetailedAnalysis: boolean;
  isAnalysisPublic: boolean;
  aiAnalysisSummary: string | null;
  aiAnalysisSummaryTeaser: string | null;
  aiDetailedAnalysis: {
    distribution: string;
    consecutifs: string;
    repetitions: string;
    annonces: string;
    dizaines_finales: string;
    rang_historique: string;
  } | null;
  aiNumeroComments: Record<number, string> | null;
  /**
   * Niveau servi (frontière du 20/10/2026) : l'anonyme ne reçoit que les faits marquants publics,
   * sans analyse ni texte IA ; `highlightsTotal` compte tous les faits du tirage.
   */
  access?: { niveau: "anonyme" | "gratuit" | "premium"; highlightsTotal: number };
};

export interface TirageAnalyseParams {
  date: string;
  startDate?: string;
  endDate?: string;
}

export const getTirageAnalyse = async (
  params: TirageAnalyseParams | string,
  init?: RequestInit
): Promise<TirageAnalyseOutput | null> => {
  // Support both string (date only) and object params
  const { date, startDate, endDate } = typeof params === 'string'
    ? { date: params, startDate: undefined, endDate: undefined }
    : params;

  const searchParams = new URLSearchParams();
  if (startDate) searchParams.set('startDate', startDate);
  if (endDate) searchParams.set('endDate', endDate);

  const queryString = searchParams.toString();
  const url = `/_api/public/tirage-analyse/${date}${queryString ? `?${queryString}` : ''}`;

  const result = await fetch(url, {
    method: "GET",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (result.status === 404) {
    return null;
  }

  if (!result.ok) {
    const errorObject = JSON.parse(await result.text()) as { error: string };
    throw new Error(errorObject.error);
  }
  return JSON.parse(await result.text()) as TirageAnalyseOutput;
};
